import { useEffect, useState } from "react";
import {
  Camera,
  User,
  MapPin,
  Briefcase,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  UploadCloud,
  Clock,
} from "lucide-react";
import {
  useGetProfileMetaMeQuery,
  useCompleteProfileMutation,
} from "../api/authApi";

import { formatDate } from "../../../utils/formatters";
const BASE_URL = "https://api.niqsng.org";

function resolveProfilePic(path?: string | null) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${BASE_URL}/storage/${path}`;
}
export const resolveMemberGrade = (
  gradeId: number | string | null | undefined
) => {
  const id = Number(gradeId);
  const grades: Record<number, string> = {
    1: "Fellow",
    2: "Member",
    3: "Probationer",
    4: "Technician",
    5: "Student",
    6: "Firm",
    7: "Graduate",
    8: "State Chapter",
    9: "Past President",
  };

  return grades[id] || "Member";
};
const getUpgradeHistory = (member: any) => {
  const milestones = [
    { label: "Registration", date: member.reg_date, id: 0 },
    { label: "Student Registration", date: member.student_date, id: 5 },
    { label: "Probationer Registration", date: member.probationer_date, id: 3 },
    { label: "Member Upgrade", date: member.member_date, id: 2 },
    { label: "Fellow Upgrade", date: member.fellow_date, id: 1 },
  ];

  // Only return milestones that have a valid date
  // return milestones.filter((m) => m.date && m.date !== "0000-00-00");
  return milestones;
};
export default function ProfilePage() {
  const { data, isLoading, refetch } = useGetProfileMetaMeQuery();
  const [updateProfile, { isLoading: saving }] = useCompleteProfileMutation();
  console.log("retuning", data?.user, "this i the returned data", data?.member);
  const user = data?.user || {};
  const member = data?.member || {};
  const states = data?.states || [];
  const sectors = data?.sectors || [];
  const chapters = data?.chapters || [];
  const allLgas = data?.local_governments || [];

  const [form, setForm] = useState<any>({});
  const [profilePreview, setProfilePreview] = useState<any>(null);
  const [signaturePreview, setSignaturePreview] = useState<any>(null);
  const [lgas, setLgas] = useState<any[]>([]);

  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setForm({
      name: user.name || "",
      email: user.email || "",
      phone: member.phone || "",
      phone2: member.phone2 || "",
      title: member.title || "",
      surname: member.surname || "",
      firstname: member.firstname || "",
      othernames: member.othernames || "",
      gender: member.gender?.trim().toLowerCase() || "",
      address: member.address || "",
      office_address: member.office_address || "",
      chapter_id: member.chapter_id || "",
      state_id: member.state_id || "",
      local_government_id: member.local_government_id || "",
      dob: member.dob || "",
      nationality: member.nationality || "",
      grade: member.grade || "",
      employment_sector_id: member.employment_sector_id || "",
      employer_name: member.employer_name || "",
      employer_state_id: member.employer_state_id || "",
    });

    if (member.state_id) {
      setLgas(allLgas.filter((l: any) => l.state_id === member.state_id));
    }
  }, [data]);

  const handleChange = (e: any) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleStateChange = (e: any) => {
    const stateId = Number(e.target.value);
    setForm({ ...form, state_id: stateId, local_government_id: "" });
    setLgas(allLgas.filter((l: any) => l.state_id === stateId));
  };

  const handleFile = (e: any, field: string) => {
    const file = e.target.files[0];
    if (!file) return;
    if (field === "pro_pic") setProfilePreview(URL.createObjectURL(file));
    if (field === "signature") setSignaturePreview(URL.createObjectURL(file));
    setForm((prev: any) => ({ ...prev, [field]: file }));
  };

  const submit = async (e: any) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);

    if (!form.state_id) {
      setError("State of Origin is required.");
      return;
    }
    if (!form.local_government_id) {
      setError("Local Government is required.");
      return;
    }
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([key, value]: any) => {
        if (value instanceof File) fd.append(key, value);
        else if (value !== null && value !== undefined && value !== "")
          fd.append(key, value);
      });
      await updateProfile(fd).unwrap();
      setSuccess(
        "Profile biological data and credentials synchronized successfully."
      );
      refetch();
    } catch (e: any) {
      setError(
        e?.data?.message ||
          "Internal Server Error: Failed to update profile registry."
      );
    }
  };

  if (isLoading)
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );

  const inputClass =
    "w-full bg-gray-50 border border-gray-100 rounded-2xl px-4 py-3 text-sm font-bold text-gray-900 focus:bg-white focus:ring-2 focus:ring-blue-100 outline-none transition-all placeholder:text-gray-300 placeholder:font-normal";
  const labelClass =
    "text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2 block ml-1";

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-20">
      {/* HEADER */}
      <div className="bg-gray-900 rounded-[2.5rem] p-8 md:p-12 text-white relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 p-10 opacity-10">
          <User size={120} />
        </div>
        <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
          <div className="relative group">
            <img
              src={
                profilePreview ||
                resolveProfilePic(user?.pro_pic) ||
                "https://ui-avatars.com/api/?name=" + user.name
              }
              className="w-32 h-32 rounded-[2.5rem] object-cover border-4 border-white/10 shadow-2xl"
            />
            <label className="absolute bottom-0 right-0 bg-blue-600 p-2.5 rounded-2xl border-4 border-gray-900 cursor-pointer hover:scale-110 transition-transform shadow-xl">
              <Camera size={18} className="text-white" />
              <input
                type="file"
                className="hidden"
                onChange={(e) => handleFile(e, "pro_pic")}
              />
            </label>
          </div>
          <div className="text-center md:text-left">
            <h1 className="text-3xl font-black uppercase tracking-tight">
              {user.name || "Member Profile"}
            </h1>
            <p className="text-blue-400 font-bold text-sm tracking-widest uppercase mt-1">
              Membership NO: {member.membership_no || "PENDING"}
            </p>
            <div className="flex flex-wrap justify-center md:justify-start gap-3 mt-4">
              {/* Displays the professional title based on the ID */}
              <span className="bg-white/10 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-white/10">
                {resolveMemberGrade(member.grade)}
              </span>

              <span className="bg-emerald-500/20 text-emerald-400 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-emerald-500/20">
                Active Account
              </span>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6">
        {success && (
          <div className="bg-emerald-50 border border-emerald-100 text-emerald-700 p-4 rounded-3xl flex items-center gap-3 animate-in fade-in zoom-in duration-300">
            <CheckCircle2 size={20} />
            <span className="text-sm font-bold">{success}</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-700 p-4 rounded-3xl flex items-center gap-3">
            <AlertCircle size={20} />
            <span className="text-sm font-bold">{error}</span>
          </div>
        )}

        {/* SECTION 1: PERSONAL BIODATA */}
        <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-8 border-b border-gray-50 pb-6">
            <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
              <User size={20} />
            </div>
            <h2 className="text-lg font-black text-gray-900 uppercase tracking-tight">
              Personal Biodata
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className={labelClass}>Title</label>
              <select
                name="title"
                value={form.title}
                onChange={handleChange}
                className={inputClass}
                disabled
              >
                <option value="">Select Title</option>
                <option value="Mr.">Mr.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Ms.">Ms.</option>
                <option value="QS.">QS.</option>
                <option value="Dr.">Dr.</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Surname</label>
              <input
                name="surname"
                value={form.surname}
                onChange={handleChange}
                className={inputClass}
                disabled={true}
              />
            </div>
            <div>
              <label className={labelClass}>Firstname</label>
              <input
                name="firstname"
                value={form.firstname}
                onChange={handleChange}
                className={inputClass}
                disabled={true}
              />
            </div>
            <div>
              <label className={labelClass}>Other Names</label>
              <input
                name="othernames"
                value={form.othernames}
                onChange={handleChange}
                className={inputClass}
                disabled={true}
              />
            </div>
            <div>
              <label className={labelClass}>Gender</label>
              <select
                name="gender"
                value={form.gender}
                onChange={handleChange}
                className={inputClass}
                disabled
              >
                <option value="">Select Gender</option>
                <option value="m">Male</option>
                <option value="f">Female</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Date of Birth</label>
              <input
                type="date"
                name="dob"
                value={form.dob}
                onChange={handleChange}
                className={inputClass}
                disabled={true}
              />
            </div>
          </div>
        </div>

        {/* SECTION 2: CONTACT & GEOGRAPHICS */}
        <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-8 border-b border-gray-50 pb-6">
            <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
              <MapPin size={20} />
            </div>
            <h2 className="text-lg font-black text-gray-900 uppercase tracking-tight">
              Contact Information
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className={labelClass}>Primary Phone</label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Secondary Phone</label>
              <input
                name="phone2"
                value={form.phone2}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className={labelClass}>Residential Address</label>
              <input
                name="address"
                value={form.address}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Office Address</label>
              <input
                name="office_address"
                value={form.office_address}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={labelClass}>
                  State of Origin <span className="text-red-500">*</span>
                </label>
                <select
                  name="state_id"
                  value={form.state_id}
                  onChange={handleStateChange}
                  className={inputClass}
                  required
                >
                  <option value="">Select State</option>
                  {states.map((s: any) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  Local Government <span className="text-red-500">*</span>
                </label>
                <select
                  name="local_government_id"
                  value={form.local_government_id}
                  onChange={handleChange}
                  className={inputClass}
                  required
                  disabled={!form.state_id}
                >
                  <option value="">
                    {form.state_id ? "Select LGA" : "Select state first"}
                  </option>
                  {lgas.map((l: any) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Member Chapter (Center)</label>
                <select
                  name="chapter_id"
                  value={form.chapter_id}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="">Select Chapter</option>
                  {chapters.map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        {/* SECTION: EMPLOYMENT REGISTRY */}
        <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-8 border-b border-gray-50 pb-6">
            <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
              <Briefcase size={20} />
            </div>
            <div>
              <h2 className="text-lg font-black text-gray-900 uppercase tracking-tight">
                Employment Status
              </h2>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                Current Professional Placement & Sector
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {form.employment_sector_id == "1" && ( // Assuming 1 is Private Practice
              <p className="text-[9px] font-bold text-blue-500 mt-2 uppercase tracking-tight flex items-center gap-1">
                <ShieldCheck size={12} /> Firm verification may be required
              </p>
            )}
            <div>
              <label className={labelClass}>Employment Sector</label>
              <select
                name="employment_sector_id" // Ensure this matches your DB column (sometimes it's sector_id)
                value={form.employment_sector_id}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="">Select Professional Sector</option>
                {sectors.map((s: any) => (
                  <option key={s.id} value={s.id || s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Employer Name */}
            <div>
              <label className={labelClass}>Employer / Firm Name</label>
              <input
                name="employer_name"
                value={form.employer_name}
                onChange={handleChange}
                placeholder="e.g. Federal Ministry of Works"
                className={inputClass}
              />
            </div>

            {/* Employer State */}
            <div>
              <label className={labelClass}>Employer Location (State)</label>
              <select
                name="employer_state_id"
                value={form.employer_state_id}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="">Select State</option>
                {states.map((s: any) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {/* SECTION: UPGRADE HISTORY & MILESTONES */}
        <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-8 border-b border-gray-50 pb-6">
            <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center">
              <Clock size={20} />
            </div>
            <div>
              <h2 className="text-lg font-black text-gray-900 uppercase tracking-tight">
                Career Progression
              </h2>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                Official NIQS Membership Upgrade History
              </p>
            </div>
          </div>

          <div className="relative pl-8 space-y-8 before:content-[''] before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-[2px] before:bg-gray-50">
            {getUpgradeHistory(member).length > 0 ? (
              getUpgradeHistory(member).map((milestone, index) => (
                <div key={index} className="relative">
                  {/* Dot Indicator */}
                  <div className="absolute -left-[27px] top-1 w-4 h-4 rounded-full bg-white border-4 border-emerald-500 z-10" />

                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 bg-gray-50/50 p-5 rounded-3xl border border-gray-100 hover:bg-white hover:shadow-md transition-all">
                    <div>
                      <span className="text-[9px] font-black text-emerald-600 uppercase tracking-[0.2em] mb-1 block">
                        Milestone Reached
                      </span>
                      <h4 className="text-sm font-black text-gray-900 uppercase">
                        {milestone.label}
                      </h4>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">
                        Effective Date
                      </span>
                      <span className="font-mono text-sm font-bold text-gray-900">
                        {formatDate(milestone.date)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-10 text-center">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                  No upgrade history found in registry.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* SECTION 3: CREDENTIALS & SIGNATURE */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-8 border-b border-gray-50 pb-6">
              <div className="w-10 h-10 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center">
                <Briefcase size={20} />
              </div>
              <h2 className="text-lg font-black text-gray-900 uppercase tracking-tight">
                Membership
              </h2>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Grade
                </span>
                <span className="text-sm font-black text-gray-900 uppercase">
                  {resolveMemberGrade(member.grade)}
                </span>
              </div>
              <div className="flex justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Reg. Date
                </span>
                <span className="text-sm font-black text-gray-900 uppercase">
                  {member.reg_date || "N/A"}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center">
                <ShieldCheck size={20} />
              </div>
              <h2 className="text-lg font-black text-gray-900 uppercase tracking-tight">
                Digital Signature
              </h2>
            </div>
            <div className="relative group border-2 border-dashed border-gray-100 rounded-3xl p-4 flex flex-col items-center justify-center min-h-[140px] hover:border-purple-200 transition-colors">
              {signaturePreview || resolveProfilePic(user?.signature) ? (
                <img
                  src={signaturePreview || resolveProfilePic(user?.signature)}
                  className="h-20 object-contain"
                />
              ) : (
                <div className="text-center">
                  <UploadCloud className="mx-auto text-gray-300 mb-2" />
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    No signature uploaded
                  </p>
                </div>
              )}
              <label className="mt-4 text-[10px] font-black text-purple-600 uppercase tracking-[0.2em] cursor-pointer hover:text-purple-800">
                Update Signature
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => handleFile(e, "signature")}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="group relative flex items-center gap-4 bg-gray-900 text-white px-10 py-5 rounded-[2rem] font-black text-xs uppercase tracking-[0.3em] hover:bg-blue-600 transition-all shadow-xl shadow-gray-200 active:scale-95 disabled:opacity-50"
          >
            {saving ? "Synchronizing..." : "Update Official Registry"}
            <div className="p-2 bg-white/10 rounded-xl group-hover:bg-white/20 transition-colors">
              <CheckCircle2 size={16} />
            </div>
          </button>
        </div>
      </form>
    </div>
  );
}
